<template>
  <div style="width: 100%; position: relative">
    <div
      v-if="isEmpty"
      class="chart-empty"
      :style="`height: ${height}px`"
    >
      暂无数据
    </div>
    <div
      ref="chart"
      :style="`height: ${height}px`"
    />
  </div>
</template>

<script>
import echarts from 'echarts';
import { merge, uniq, isPlainObject, isArray, isEmpty } from 'lodash';

import {
  layoutStyle,
  tooltipStyle,
  xAxisStyle,
  yAxisStyle,
  seriesStyle,
} from './config/theme';
import { getMaxIntByList, getMinIntByList, fenToYuan } from '@/common/number/';

export default {
  props: {
    dataSource: {
      type: [Array, Object],
      default() {
        return {};
      },
    },

    options: {
      type: Object,
      default() {
        return {};
      },
    },

    height: {
      type: [Number, String],
      default: 200,
    },
  },

  computed: {
    isEmpty() {
      return isEmpty(this.dataSource);
    },
  },

  watch: {
    dataSource(newVal) {
      this.render();
    },
  },

  mounted() {
    this.chart = echarts.init(this.$refs.chart);
    this.render();
    window.addEventListener('resize', this.resize, false);
  },

  destroyed() {
    window.removeEventListener('resize', this.resize, false);
  },

  methods: {
    render() {
      let option = null;
      if (this.dataSource.type === 'pie') {
        option = this.getPieOptions();
      } else {
        option = this.getOptions();
      }
      if (option) {
        const op = merge(option, this.options);
        console.dir(op);
        this.chart.setOption(op, true);
      } else {
        this.chart.clear();
      }
    },

    resize() {
      this.chart.resize();
    },

    getPieOptions() {
      const options = {
        ...layoutStyle,
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)',
        },
        legend: {
          orient: 'vertical',
          left: 10,
          data: [],
        },
        series: [],
      };

      const { base, compare } = this.dataSource;
      if (base) {
        const seriesData = [];
        const legendData = [];
        base.forEach((item) => {
          legendData.push(item.xAxis);
          seriesData.push({
            value: item.series,
            name: item.xAxis,
            _raw: item,
          });
        });

        const series = {
          name: base[0] ? base[0].label : '',
          type: 'pie',
          radius: ['50%', '90%'],
          center: ['25%', '50%'],
          avoidLabelOverlap: false,
          label: {
            normal: {
              show: false,
              position: 'center',
              formatter(params) {
                return `${params.name}\n\n${params.data._raw._total}`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
            },
          },
          labelLine: {
            normal: {
              show: false,
            },
          },
          data: seriesData,
        };

        options.series.push(series);
        options.legend.data = legendData;
      }

      if (compare) {
        const seriesData = [];
        const legendData = [];
        compare.forEach((item) => {
          legendData.push(item.xAxis);
          seriesData.push({
            value: item.series,
            name: item.xAxis,
            _raw: item,
          });
        });

        const series = {
          name: compare[0] ? compare[0].label : '',
          type: 'pie',
          radius: ['50%', '90%'],
          center: ['75%', '50%'],
          avoidLabelOverlap: false,
          label: {
            normal: {
              show: false,
              position: 'center',
              formatter(params) {
                return `${params.name}\n\n${params.data._raw._total}`;
              },
            },
            emphasis: {
              show: true,
              textStyle: {
                fontSize: '14',
                fontWeight: 'bold',
              },
            },
          },
          labelLine: {
            normal: {
              show: false,
            },
          },
          data: seriesData,
        };

        options.series.push(series);
        options.legend.data = legendData;
      }

      return options;
    },

    getOptions() {
      const option = {
        ...layoutStyle,
        tooltip: { ...tooltipStyle },
        xAxis: [],
        yAxis: [],
        series: [],
      };
      const { base } = this.dataSource;

      if (base) {
        // 多种类型图表
        if (isPlainObject(base)) {
          const types = Object.keys(base);
          types.forEach((type, index) => {
            const data = base[type];
            const res = this.getData(data, index);

            option.xAxis[0] = {
              ...xAxisStyle,
              data: res.xAxis,
              ...this.options.xAxis,
            };

            option.yAxis.push({
              ...yAxisStyle,
              ...res.yAxis,
            });

            option.series.push({
              ...seriesStyle,
              ...res.series,
            });
          });
          return option;
          // 单个类型图表
        } else if (isArray(base)) {
          let xAxis = [];
          let min = [];
          let max = [];
          base.forEach((item) => {
            if (item instanceof Array) {
              const data = this.getData(item);

              xAxis = xAxis.concat(data.xAxis);
              min.push(data.yAxis.min);
              max.push(data.yAxis.max);

              option.series.push({
                ...seriesStyle,
                ...data.series,
              });
            }
          });

          option.xAxis[0] = {
            ...xAxisStyle,
            data: uniq(xAxis),
            ...this.options.xAxis,
          };

          min = getMinIntByList(min);
          max = getMaxIntByList(max);

          option.yAxis.push({
            ...yAxisStyle,
            min,
            max,
            splitNumber: 5,
            interval: (max - min) / 5,
          });

          return option;
        }
      }

      return null;
    },

    getData(list, index) {
      const xAxis = [];
      const seriesData = [];

      let label = '';
      let type = 'bar';

      list.forEach((item) => {
        xAxis.push(item.xAxis);
        const series =
          item.valueType === 'money' ? fenToYuan(item.series) : item.series;
        seriesData.push({
          name: label,
          value: series,
          _raw: item,
        });
        label = item.label;
        type = item.type;
      });
      const yAxis = this.calYAxisScale(seriesData);

      return {
        xAxis,
        yAxis,
        series: {
          name: label,
          type: type,
          yAxisIndex: index,
          data: seriesData,
        },
      };
    },

    // 计算y轴刻度
    calYAxisScale(numbers) {
      const nums = numbers.map((item) => item.value);
      const min = getMinIntByList(nums);
      const max = getMaxIntByList(nums);
      const splitNumber = 5;

      return {
        min,
        max,
        splitNumber,
        interval: (max - min) / splitNumber,
      };
    },
  },
};
</script>

<style lang="less" scoped>
.chart {
  &-empty {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: #999;
    position: absolute;
    top: 0;
    left: 0;
  }
}
</style>